import { useEffect, useState } from "react";
import type { Translate } from "./strings";

const MIN = 4;
const MAX = 6;
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;

export interface PinPadProps {
  readonly t: Translate;
  /** Called with 4 to 6 digits. The parent remounts the pad (`key`) to clear it. */
  readonly onSubmit: (pin: string) => void;
  readonly busy: boolean;
  /** No more input, e.g. a locked profile. */
  readonly disabled: boolean;
}

/** A big numeric pad. Physical keyboards work too: digits, Backspace and Enter. */
export function PinPad({ t, onSubmit, busy, disabled }: PinPadProps) {
  const [digits, setDigits] = useState("");
  const inactive = busy || disabled;

  const press = (digit: string) => {
    if (!inactive) setDigits((d) => (d.length < MAX ? d + digit : d));
  };
  const erase = () => {
    if (!inactive) setDigits((d) => d.slice(0, -1));
  };
  const submit = () => {
    if (!inactive && digits.length >= MIN) onSubmit(digits);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement) return;
      if (/^\d$/.test(event.key)) press(event.key);
      else if (event.key === "Backspace") erase();
      else if (event.key === "Enter") submit();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  });

  return (
    <div className="auth-pad">
      <div className="auth-pad-dots" aria-hidden="true">
        {Array.from({ length: MAX }, (_, i) => (
          <span
            key={i}
            className={`auth-pad-dot${i < digits.length ? " auth-pad-dot-filled" : ""}${
              i >= MIN ? " auth-pad-dot-optional" : ""
            }`}
          />
        ))}
      </div>
      <p className="auth-visually-hidden" aria-live="polite">
        {t("pad.entered", { count: String(digits.length) })}
      </p>
      <div className="auth-pad-keys">
        {KEYS.map((digit) => (
          <button
            key={digit}
            type="button"
            className="auth-pad-key"
            disabled={inactive}
            onClick={() => {
              press(digit);
            }}
          >
            {digit}
          </button>
        ))}
        <button
          type="button"
          className="auth-pad-key auth-pad-key-action"
          aria-label={t("pad.delete")}
          disabled={inactive || digits.length === 0}
          onClick={erase}
        >
          ⌫
        </button>
        <button
          type="button"
          className="auth-pad-key"
          disabled={inactive}
          onClick={() => {
            press("0");
          }}
        >
          0
        </button>
        <button
          type="button"
          className="auth-pad-key auth-pad-key-go"
          disabled={inactive || digits.length < MIN}
          onClick={submit}
        >
          {t("pad.submit")}
        </button>
      </div>
    </div>
  );
}
